import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { AdminFeedbacksApi, AdminTripsApi  } from './adminFeedbacksApi';

// Reuse types from your API file or define here
export type FeedbackItem = {
  _id: string;
  description?: string;
  type?: 'staff' | 'guest';
  action?: boolean;
  actionStatus?: 'allocated_and_acknowledged' | 'allocated_but_not_acknowledged' | 'unallocated';
  createdAt?: string;
  additionalInfo?: {
    sentiment?: 'positive' | 'negative' | 'neutral';
    sentimentScore?: number;
    tripId?: string;
    description?: string;
    category?: string;
    subCategory?: string | null;
    importance?: 'High' | 'Medium' | 'Low';
  };
};

export type PageInfo = {
  totalItems: number;
  page: number;
  size: number;
  totalPages: number;
};

type TripFeedbacksState = {
  byTrip: Record<string, { items: FeedbackItem[]; pageInfo: PageInfo }>;
  isLoading: boolean;
  error?: string | null;
};

const initialState: TripFeedbacksState = {
  byTrip: {},
  isLoading: false,
  error: null,
};

/** THUNKS **/
export const tryToListFeedbacksByTrip = createAsyncThunk<
  { tripId: string; items: FeedbackItem[]; pageInfo: PageInfo },
  { tripId: string; page: number; size: number }
>('tripFeedbacks/tryToListFeedbacksByTrip', async ({ tripId, page, size }, { rejectWithValue }) => {
  try {
    const res = await AdminFeedbacksApi.listByTrip(tripId, page, size);
    const list = res?.data?.data;
    return {
      tripId,
      items: list?.items ?? [],
      pageInfo: list?.pageInfo ?? { totalItems: 0, page, size, totalPages: 0 },
    };
  } catch (err) {
    const e = err as AxiosError<any>;
    return rejectWithValue(e?.response?.data?.message || e.message);
  }
});

export const tryToUpdateFeedbackDescription = createAsyncThunk<
  { id: string; description: string },
  { id: string; description: string }
>('tripFeedbacks/tryToUpdateFeedbackDescription', async ({ id, description }, { rejectWithValue }) => {
  try {
    await AdminFeedbacksApi.updateDescription(id, description);
    return { id, description };
  } catch (err) {
    const e = err as AxiosError<any>;
    return rejectWithValue(e?.response?.data?.message || e.message);
  }
});

export const tryToDeleteFeedback = createAsyncThunk<{ id: string }, { id: string }>(
  'tripFeedbacks/tryToDeleteFeedback',
  async ({ id }, { rejectWithValue }) => {
    try {
      await AdminFeedbacksApi.deleteFeedback(id);
      return { id };
    } catch (err) {
      const e = err as AxiosError<any>;
      return rejectWithValue(e?.response?.data?.message || e.message);
    }
  },
);

// ⬇️ NEW: create feedback for a trip
export const tryToCreateFeedback = createAsyncThunk<
  { tripId: string; items: FeedbackItem[] },
  {
    tripId: string;
    payload: {
      description: string;
      type?: 'staff' | 'guest';
      sentiment?: 'positive' | 'negative' | 'neutral';
      action?: boolean;
      actionStatus?: 'allocated_and_acknowledged' | 'allocated_but_not_acknowledged' | 'unallocated';
      originalMessage?: string;
      additionalInfo?: Record<string, any>;
      files?: Array<{ name: string; url: string }>;
    };
  }
>('tripFeedbacks/tryToCreateFeedback', async ({ tripId, payload }, { rejectWithValue }) => {
  try {
    const res = await AdminFeedbacksApi.createForTrip(tripId, payload);
    const created = res?.data?.data; // backend returns { success, data }
    const items = Array.isArray(created) ? created : created ? [created] : [];
    return { tripId, items: items as FeedbackItem[] };
  } catch (err: any) {
    const msg = err?.response?.data?.message || err.message;
    return rejectWithValue(msg);
  }
});

export const tryToDeleteAssignedCabin = createAsyncThunk<
  { tripId: string; assignedCabinId: string },
  { tripId: string; assignedCabinId: string }
>('tripFeedbacks/tryToDeleteAssignedCabin', async ({ tripId, assignedCabinId }, { rejectWithValue }) => {
  try {
    await AdminTripsApi.deleteAssignedCabin(tripId, assignedCabinId);
    return { tripId, assignedCabinId };
  } catch (err) {
    const e = err as AxiosError<any>;
    return rejectWithValue(e?.response?.data?.message || e.message);
  }
});

// ⬇️ NEW: update feedback importance
export const tryToUpdateFeedbackImportance = createAsyncThunk<
  { id: string; importance: 'High' | 'Medium' | 'Low' },
  { id: string; importance: 'High' | 'Medium' | 'Low'; originalImportance?: 'High' | 'Medium' | 'Low' }
>('tripFeedbacks/tryToUpdateFeedbackImportance', async ({ id, importance, originalImportance }, { rejectWithValue }) => {
  try {
    await AdminFeedbacksApi.updateImportance(id, { importance, originalImportance });
    return { id, importance };
  } catch (err) {
    const e = err as AxiosError<any>;
    return rejectWithValue(e?.response?.data?.message || e.message);
  }
});

/** SLICE **/
const slice = createSlice({
  name: 'tripFeedbacks',
  initialState,
  reducers: {
    resetTripFeedbacks(state) {
      state.byTrip = {};
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(tryToListFeedbacksByTrip.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(tryToListFeedbacksByTrip.rejected, (state, action) => {
      state.isLoading = false;
      state.error = (action.payload as string) || action.error.message || 'Failed to fetch feedbacks';
    });
    builder.addCase(
      tryToListFeedbacksByTrip.fulfilled,
      (state, action: PayloadAction<{ tripId: string; items: FeedbackItem[]; pageInfo: PageInfo }>) => {
        const { tripId, items, pageInfo } = action.payload;
        state.isLoading = false;
        state.byTrip[tripId] = { items, pageInfo };
      },
    );

    // Update description (optimistic apply in state)
    builder.addCase(tryToUpdateFeedbackDescription.fulfilled, (state, action) => {
      const { id, description } = action.payload;
      Object.values(state.byTrip).forEach((bucket) => {
        const idx = bucket.items.findIndex((f) => f._id === id);
        if (idx >= 0) {
          const prev = bucket.items[idx];
          bucket.items[idx] = {
            ...prev,
            description,
            additionalInfo: { ...(prev.additionalInfo || {}), description },
          };
        }
      });
    });

    // Delete (optimistic remove)
    builder.addCase(tryToDeleteFeedback.fulfilled, (state, action) => {
      const { id } = action.payload;
      Object.values(state.byTrip).forEach((bucket) => {
        const before = bucket.items.length;
        bucket.items = bucket.items.filter((f) => f._id !== id);
        if (bucket.pageInfo?.totalItems != null && before !== bucket.items.length) {
          bucket.pageInfo.totalItems = Math.max(0, bucket.pageInfo.totalItems - 1);
        }
      });
    });

    // ⬇️ NEW: Create (prepend into current list and bump counts)
    builder.addCase(tryToCreateFeedback.fulfilled, (state, action) => {
      const { tripId, items } = action.payload;
      const bucket = state.byTrip[tripId];
      if (!bucket) return; // if not loaded yet, component can refetch

      if (!items?.length) return;
      const existing = bucket.items || [];
      const existingIds = new Set(existing.map((f) => f._id));
      const newUniqueItems = items.filter((f) => f?._id && !existingIds.has(f._id));

      bucket.items = [...newUniqueItems, ...existing];
      if (bucket.pageInfo) {
        const nextTotal = (bucket.pageInfo.totalItems ?? 0) + newUniqueItems.length;
        bucket.pageInfo.totalItems = nextTotal;
        if (bucket.pageInfo.size > 0) {
          bucket.pageInfo.totalPages = Math.ceil(nextTotal / bucket.pageInfo.size);
        }
      }
    });

    // ⬇️ NEW: Update importance (optimistic update)
    builder.addCase(tryToUpdateFeedbackImportance.fulfilled, (state, action) => {
      const { id, importance } = action.payload;
      Object.values(state.byTrip).forEach((bucket) => {
        const idx = bucket.items.findIndex((f) => f._id === id);
        if (idx >= 0) {
          const prev = bucket.items[idx];
          bucket.items[idx] = {
            ...prev,
            additionalInfo: {
              ...(prev.additionalInfo || {}),
              importance,
            },
          };
        }
      });
    });

     builder.addCase(tryToDeleteAssignedCabin.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(tryToDeleteAssignedCabin.rejected, (state, action) => {
      state.isLoading = false;
      state.error = (action.payload as string) || action.error.message || 'Failed to delete assigned cabin';
    });
    builder.addCase(tryToDeleteAssignedCabin.fulfilled, (state) => {
      state.isLoading = false;
      // no state change here since this slice holds feedbacks; your UI can refetch the trip or another slice can handle trip state
    });
  },
});

export const { resetTripFeedbacks } = slice.actions;
export default slice.reducer;
